import { useEffect, useMemo, useState } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import kanbanService from '../../service/kanban.service';
import { useLoader } from '../../provider/LoaderProvider';

import useParam from '../../context/ParamContext';
import useUsers from '../../context/UsersContext';
import { KanbanType, conditions, FunnelViewType, KanbanDialog } from '../../config/kanban';
import { generateAutocompleteItems, generateFilter, getKanbanFieldsByControl } from '../../utils/helper';
import { ColorPicker } from '../../config/ColorPicker';
import useSaveView from '../../hooks/useSaveView';
import useView from '../../hooks/useView';
import Card from './Card';
import { AutoCompleteSelectUI, DebouncedInputUI, FormLabelUI, SelectUI, TooltipUI } from '../Interface';
import Dialog from './Dialog';
import masterDataService from '../../service/masterData.service';
import { IconButton } from '@mui/material';
import StatusDialog from '../Status/StatusDialog';
import { ProjectPage } from '../Project/ProjectPage';
import { ResourceOnePage } from '../Resources/NewResourceOne/ResourceOnePage';
import { TaskFeedbackPage } from '../TaskFeedback/TaskFeedbackPage';
import { SimpleTaskForm } from '../NewSimpleTask/SimpleTaskForm';
import { useProject } from '../../hooks/useProject';
import { useResourceOne } from '../../hooks/useResourceOne';
import { useTaskFeedback } from '../../hooks/useTaskFeedback';
import { useSimpleTask } from '../../hooks/useSimpleTask';

const ComponentMap = {
  [KanbanType.PROJECT]: ProjectPage,
  [KanbanType.RESOURCE]: ResourceOnePage,
  [KanbanType.FEEDBACK]: TaskFeedbackPage,
  [KanbanType.TASK]: SimpleTaskForm,
};

export const FunnelView = () => {
  const projectState = useProject();
  const resourceOneState = useResourceOne();
  const taskFeedbackState = useTaskFeedback();
  const simpleTaskState = useSimpleTask();
  const { getUser, user } = useUsers();
  const saveView = useSaveView(user);
  const funnelViewFilter = useView('funnelViewFilter', user);
  const { getParam, params } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [kanbanData, setKanbanData] = useState([]);
  const [item, setItem] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterFormState, setFilterFormState] = useState({});
  const [loading, setLoading] = useState(null);
  const [type, setType] = useState('');
  const [defaultFilterFields, setDefaultFilterFields] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [taskChanged, setTaskChanged] = useState(false);
  const [status, setStatus] = useState({});
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    user: [],
    company: [],
    project: [],
    responsible: [],
  });
  const [refetch, setRefetch] = useState(false);
  const [dialogParams, setDialogParams] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [query, setQuery] = useState({
    filter: [],
  });

  useEffect(() => {
    const kanbanDialogConfig = KanbanDialog.find((card) => card.type === type);
    const paramName = kanbanDialogConfig?.paramName;

    if (params[paramName]) {
      const parsedParams = JSON.parse(params[paramName]);
      setDialogParams(parsedParams);
    }
  }, [params, type]);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getParam();
    initDefaultValues();
  }, []);

  useEffect(() => {
    if (type !== '') return;

    setType(KanbanType.TASK);
  }, [type]);

  useEffect(() => {
    if (!Object.keys(funnelViewFilter).length) return;

    const savedType = funnelViewFilter?.funnelViewType;
    if (!savedType) return;

    setType(savedType);
  }, [funnelViewFilter]);

  useEffect(() => {
    if (taskChanged) {
      getItemsByType();
      setTaskChanged(false);
    }
  }, [taskChanged]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const defaultFilter = [
      {
        field: 'deletedBy',
        value: null,
      },
    ];

    const currentConditions = conditions[type || KanbanType.TASK];
    const newFilter = generateFilter(filterFormState, currentConditions);
    setQuery((prev) => {
      return { ...prev, filter: [...defaultFilter, ...newFilter] };
    });
    setDefaultFilterFields(defaultFilter);
  }, [filterFormState]);

  useEffect(() => {
    if (refetch) {
      getItemsByType();
    }
  }, [refetch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (defaultFilterFields.length > 0 && query.filter.length > 0 && !isLoading) {
        getItemsByType();
      }
    }, 100);

    return () => clearTimeout(delayDebounceFn);
  }, [query, defaultFilterFields.length, isLoading]);

  const getCurrentViewField = () => {
    if (type === KanbanType.PROJECT) {
      return projectState.viewField;
    } else if (type === KanbanType.RESOURCE) {
      return resourceOneState.viewField;
    } else if (type === KanbanType.FEEDBACK) {
      return taskFeedbackState.viewField;
    } else if (type === KanbanType.TASK) {
      return simpleTaskState.viewField;
    }
  };

  const SpecificComponent = ComponentMap[type] ?? null;

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .userGroupsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          company: resp[1],
          projectType: resp[2],
          project: resp[3],
        };

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          projectType: generateAutocompleteItems(aData.projectType, 'name', 'id'),
          project: generateAutocompleteItems(aData.project, ['projectName', 'orderNumber'], 'projectId'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        setIsLoading(false);
        //hideLoader();
      });
  };

  const getStatusByType = () => {
    setStatusDialogOpen(true);
    showLoader();
    kanbanService
      .getStatusByType(type)
      .then((data) => {
        setStatus(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getItemsByType = () => {
    showLoader();
    kanbanService
      .getItemsByType(type, query, 'N')
      .then((data) => {
        setKanbanData(data);
        setRefetch(false);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getItemById = (id) => {
    showLoader();
    kanbanService
      .getItemById(type, id)
      .then((data) => {
        setItem(data);
        setDialogOpen(true);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleDropAndStatusUpdate = (statusId, kanbanItem) => {
    setLoading(kanbanItem.id);
    const newStatus = kanbanData.find((kanbanColumn) => kanbanColumn.itemId === statusId);

    const updatedColumn = {
      ...kanbanItem,
      statusId: statusId,
      propertyId: kanbanItem.propertyId ?? null,
      preparedness: newStatus.preparedness ? Number(newStatus.preparedness) : null,
      status: {
        ...kanbanItem.status,
        itemName: newStatus.itemName,
        color: newStatus.color,
        statusId: newStatus.itemId,
      },
    };

    kanbanService
      .updateItemsByType(type, kanbanItem.id, statusId, updatedColumn)
      .then((data) => {
        setKanbanData((prev) => {
          return prev.map((kanbanColumn) => {
            if (kanbanColumn.itemId === data.statusId) {
              return {
                ...kanbanColumn,
                items: [data, ...kanbanColumn.items.filter((item) => item.id !== kanbanItem.id)],
              };
            } else if (kanbanColumn.itemId === kanbanItem.statusId) {
              return {
                ...kanbanColumn,
                items: kanbanColumn.items.filter((item) => item.id !== kanbanItem.id),
              };
            }
            return kanbanColumn;
          });
        });
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const onDragStart = (event, kanbanItem) => {
    event.dataTransfer.setData('kanbanItem', JSON.stringify(kanbanItem));

    const dragImage = event.target.cloneNode(true);
    dragImage.style.position = 'absolute';
    dragImage.style.opacity = '1';
    dragImage.style.pointerEvents = 'none';
    dragImage.style.transform = 'translateY(-50%)';
    document.body.appendChild(dragImage);
    event.dataTransfer.setDragImage(dragImage, 0, 0);

    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  };

  const onDragOver = (event) => {
    event.preventDefault();
  };

  const onDrop = (event, statusId) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    const kanbanItem = JSON.parse(event.dataTransfer.getData('kanbanItem'));

    if (kanbanItem.statusId !== statusId) {
      handleDropAndStatusUpdate(statusId, kanbanItem);
    }
  };

  const calcWidth = (index) => {
    if (index === 0) return 'calc(100% - 30px)';
    return windowWidth < 1400 ? `calc(100% - ${150 * index}px)` : `calc(100% - ${200 * index}px)`;
  };

  const handleTextLikeChange = (fieldName, newValue) => {
    setFilterFormState((prev) => ({
      ...prev,
      [fieldName]: newValue,
    }));
  };

  const handleAutocompleteChange = (fieldName, newVal, reason, control) => {
    setFilterFormState((prev) => {
      if (control === 'multiAutocomplete') {
        return {
          ...prev,
          [fieldName]: reason === 'clear' ? [] : (newVal ?? []).map((v) => v.value),
        };
      }

      return {
        ...prev,
        [fieldName]: reason === 'clear' ? '' : newVal?.value ?? '',
      };
    });
  };

  return (
    <div className="bg-gray-100 relative pt-1 w-full px-3 pb-3" style={{ minHeight: 'calc(100vh - 65px)' }}>
      <div className="absolute md:fixed top-[10px]  md:top-1.5 md:right-[170px] mt-1 z-0 md:z-40 ">
        <IconButton onClick={() => getStatusByType()}>
          <SettingsIcon sx={{ fontSize: '1.8rem' }} />
        </IconButton>
      </div>
      <div className={`md:left-80 z-0 md:z-40  absolute top-20 md:-top-14 left-5`}>
        <div className="flex flex-col md:flex-row w-full md:gap-3">
          <div className="w-full md:w-[110px]">
            <SelectUI
              label={<FormLabelUI text="Nézet" textColor="md:text-white" />}
              option={FunnelViewType}
              value={type}
              onChange={(e) => {
                const value = e.target.value;
                setType(value);
                saveView('funnelViewFilter', { funnelViewType: value });
              }}
            />
          </div>
          <div className="w-[160px]">
            {getKanbanFieldsByControl(type, 'textLike').map(([fieldName, cfg]) => (
              <DebouncedInputUI
                key={fieldName}
                label={<FormLabelUI text={cfg.label} textColor="md:text-white" />}
                debounceMs={800}
                fieldName={fieldName}
                setQuickFilterSearchValue={handleTextLikeChange}
                quickFilterSearchValue={filterFormState[fieldName]}
              />
            ))}
          </div>
          {getKanbanFieldsByControl(type, 'autocomplete').map(([fieldName, cfg]) => {
            const disabled = cfg.getDisabled({});

            if (cfg.shouldShow && !cfg.shouldShow(params)) return null;

            return (
              <div key={fieldName} className={cfg.widthClass}>
                <AutoCompleteSelectUI
                  variant="standard"
                  onChange={(_e, newVal, reason) => handleAutocompleteChange(fieldName, newVal, reason, cfg.control)}
                  selectedValue={filterFormState[fieldName]}
                  selectedLabelValue={
                    autoCompleteOptions[cfg.optionsKey]?.find((o) => o.value === filterFormState[fieldName])?.label
                  }
                  label={<FormLabelUI text={cfg.label} textColor="md:text-white" />}
                  dataset={autoCompleteOptions[cfg.optionsKey]}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <div className={`flex flex-col justify-center w-full mt-4 pt-64 md:pt-0 md:items-center`}>
          {kanbanData.map((kanban, index) => {
            const statusColor = ColorPicker.find((col) => col.colorName === kanban.color);
            const secondaryColor = statusColor ?? kanban?.color;
            return (
              <div
                key={kanban.itemId}
                className={`flex flex-row items-center`}
                style={{
                  width: calcWidth(index),
                }}
              >
                <div
                  className="text-center font-bold p-2 min-w-[230px] max-w-[230px] mr-2 relative -left-3 truncate"
                  style={{ color: statusColor ? statusColor?.textColor : secondaryColor }}
                >
                  <div className="-mt-2 truncate">
                    <TooltipUI title={kanban.itemName} className="cursor-pointer">
                      {kanban.itemName}
                    </TooltipUI>
                  </div>
                  <div>
                    <span className="text-5xl">{kanban.items.length}</span> db
                  </div>
                </div>

                <div
                  onDragOver={(event) => onDragOver(event)}
                  onDrop={(event) => onDrop(event, kanban.itemId)}
                  style={{
                    minWidth: 'calc(100% - 230px)',
                  }}
                >
                  <div
                    className={` overflow-y-auto custom-scrollbar h-[140px]`}
                    style={{
                      maxHeight: 'calc(100vh - 205px)',
                      backgroundColor: statusColor ? statusColor?.kanbanBgColor : secondaryColor,
                    }}
                  >
                    <div className="flex flex-wrap gap-x-2 gap-y-2 p-2">
                      {kanban.items.map((data) => {
                        return (
                          <Card
                            type={type}
                            key={data.id}
                            data={data}
                            onDragStart={onDragStart}
                            getItemById={getItemById}
                            statusColor={statusColor}
                            secondaryColor={secondaryColor}
                            params={params}
                            loading={loading === data.id ? loading : null}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Dialog
        open={dialogOpen}
        data={item}
        handleClose={() => {
          setDialogOpen(false);
          setItem({});
        }}
        type={type}
        params={params}
        changed={{ taskChanged, setTaskChanged }}
        getCurrentViewField={getCurrentViewField}
        SpecificComponent={SpecificComponent}
        dialogParams={dialogParams}
      />

      <StatusDialog
        open={statusDialogOpen}
        handleClose={() => setStatusDialogOpen(false)}
        data={status}
        setDataChanged={setTaskChanged}
      />
    </div>
  );
};
